import { cn, Text } from '@mntn/ui-components'
import { useState } from 'react'
import { AllocationBreakdown } from '@/components/shared/allocation-breakdown'
import { AllocationStatusBadge } from '@/components/shared/allocation-status-badge'
import { Badge } from '@/components/shared/badge'
import { SpendMeter } from '@/components/shared/spend-meter'
import { Tooltip } from '@/components/shared/tooltip'
import type { CreateCampaignCallbacks } from '@/components/shared/types'
import { getAllocationChecks, hasSpendError } from '@/components/shared/utils'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'
import { useTriggerAnimation } from '@/hooks/use-trigger-animation'
import { AirDateInfo } from './air-date-info'
import { CampaignActions } from './campaign-actions'
import { DealInfo } from './deal-info'
import { FlightDateRange } from './flight-date-range'

interface Props extends CreateCampaignCallbacks {
  orderItem: OrderItem
  handleSelectOrderItem: () => void
  handleToggleRow: () => void
  isVisible?: boolean
  animationDelay?: number
  isLoading?: boolean
  isDisabled?: boolean
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const OrderItemGridCard: React.FC<Props> = ({
  orderItem,
  handleSelectOrderItem,
  handleToggleRow,
  onCreateCampaignV2,
  isVisible = true,
  animationDelay = 0,
  isLoading = false,
  isDisabled = false,
}) => {
  const [hasAnimated, setHasAnimated] = useState(false)

  useTriggerAnimation(isVisible && !hasAnimated, setHasAnimated, animationDelay)

  const {
    airStartTime,
    airEndTime,
    flightStartTime,
    flightEndTime,
    offeringName,
    coverMediaUrl,
    campaignGroups,
    budgetMetrics: {
      delivered: spendAmount,
      total: totalBudget,
      unallocated: unallocatedBudget,
      ineffective: ineffectiveBudget,
      effective: effectiveBudget,
    },
  } = orderItem

  const { isFullyAllocated, neverAllocatedAmount, hasUnderDelivery, showAllocationWarning, underDeliveredAmount } =
    getAllocationChecks({
      effectiveBudget,
      ineffectiveBudget,
      unallocatedBudget,
    })

  const allocationBadgeBorderClassName = 'absolute top-3 left-3 z-10'
  const allocationBadgeClassName = 'w-10 h-10'

  return (
    <div
      className={cn(
        'space-y-1 transition-all duration-500 ease-out w-[388px]',
        hasAnimated && isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95',
      )}
    >
      <div className="relative border-glass p-[1px] overflow-hidden">
        <div className="bg-surface-bg-inverse overflow-x-auto">
          <div className="flex flex-col gap-4">
            <div className="relative">
              <DealInfo
                offeringName={offeringName}
                coverMediaUrl={coverMediaUrl}
                onViewDetails={handleSelectOrderItem}
                layout="grid"
              />
              {orderItem.isTest && (
                <Badge
                  label="Test"
                  type="warning"
                  variant="solid"
                  shape="square"
                  textVariant="base-xss"
                  className="absolute top-3 right-3 z-10 px-2 py-0.5"
                />
              )}
              {/* Allocation badge — top-left of card image */}
              {isFullyAllocated && (
                <AllocationStatusBadge
                  status="allocated"
                  className={allocationBadgeClassName}
                  borderClassName={allocationBadgeBorderClassName}
                />
              )}
              {showAllocationWarning && (
                <Tooltip
                  variant="warning"
                  side="top"
                  trigger={
                    <AllocationStatusBadge
                      status="warning"
                      className={allocationBadgeClassName}
                      borderClassName={allocationBadgeBorderClassName}
                    />
                  }
                  content={
                    <div className="p-3 flex flex-col gap-1 max-w-xs">
                      <Text variant="base-sb" className="text-header">
                        Needs Allocation
                      </Text>
                      <AllocationBreakdown
                        unallocatedBudget={unallocatedBudget}
                        underDeliveredAmount={underDeliveredAmount}
                        neverAllocatedAmount={neverAllocatedAmount}
                        hasUnderDelivery={hasUnderDelivery}
                        textClassName="text-primary"
                      />
                    </div>
                  }
                />
              )}
              <div className="px-4 -mt-4 relative">
                <Text variant="h5" className="text-header-inverse truncate whitespace-nowrap overflow-hidden">
                  {offeringName}
                </Text>
                <div className="flex items-center justify-between">
                  <AirDateInfo airStartTime={airStartTime} airEndTime={airEndTime} variant="grid" />
                  <Text variant="base-sm" className="text-secondary shrink-0">
                    {campaignGroups.length} Campaign
                    {campaignGroups.length !== 1 ? 's' : ''}
                  </Text>
                </div>
              </div>
            </div>
            <div className="px-4 pb-4 flex flex-col gap-2">
              <SpendMeter
                spendAmount={spendAmount}
                totalBudget={totalBudget}
                hasSpendError={hasSpendError(orderItem)}
                inverse
              />
              <FlightDateRange flightStartTime={flightStartTime} flightEndTime={flightEndTime} />
              <CampaignActions
                isLoading={isLoading}
                isDisabled={isDisabled}
                onCreateCampaignV2={onCreateCampaignV2}
                handleToggleRow={handleToggleRow}
                orderItem={orderItem}
                isExpanded={false}
                layout="grid"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
